import React, { useState, useEffect } from 'react'
import { Building2, Plus, Edit, Trash2, Save, X, ExternalLink } from 'lucide-react'

interface Tenant {
  id: string
  name: string
  domain: string
  netlifySiteId?: string
  createdAt: string
  updatedAt: string
  settings?: {
    customDomain?: string
    branchPrefix?: string
    primaryColor?: string
    logo?: string
  }
}

const TenantManager = () => {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [formData, setFormData] = useState<Partial<Tenant>>({
    name: '',
    domain: '',
    netlifySiteId: '',
    settings: {}
  })

  useEffect(() => {
    loadTenants()
  }, [])

  const loadTenants = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/tenants')
      if (response.ok) {
        const data = await response.json()
        setTenants(data.filter((t: any) => t !== null))
      }
    } catch (error) {
      console.error('Failed to load tenants:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    try {
      const response = await fetch('/api/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        await loadTenants()
        setCreating(false)
        setFormData({ name: '', domain: '', netlifySiteId: '', settings: {} })
      }
    } catch (error) {
      console.error('Failed to create tenant:', error)
    }
  }

  const handleUpdate = async (tenantId: string) => {
    try {
      const response = await fetch(`/api/tenants?id=${tenantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        await loadTenants()
        setEditing(null)
        setFormData({ name: '', domain: '', netlifySiteId: '', settings: {} })
      }
    } catch (error) {
      console.error('Failed to update tenant:', error)
    }
  }

  const handleDelete = async (tenantId: string) => {
    if (!confirm('Delete this tenant? This action cannot be undone.')) return

    try {
      const response = await fetch(`/api/tenants?id=${tenantId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        await loadTenants()
      }
    } catch (error) {
      console.error('Failed to delete tenant:', error)
    }
  }

  const startEdit = (tenant: Tenant) => {
    setEditing(tenant.id)
    setFormData(tenant)
  }

  const cancelEdit = () => {
    setEditing(null)
    setCreating(false)
    setFormData({ name: '', domain: '', netlifySiteId: '', settings: {} })
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Building2 className="h-5 w-5 mr-2" />
            Tenant Management
          </h2>
          <p className="text-sm text-gray-600 mt-1">Manage multi-tenant organizations and their trust centers</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Tenant
        </button>
      </div>

      {/* Create Form */}
      {creating && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-4">Create New Tenant</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Acme Corporation"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="acme.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Netlify Site ID (Optional)</label>
              <input
                type="text"
                value={formData.netlifySiteId || ''}
                onChange={(e) => setFormData({ ...formData, netlifySiteId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Will be auto-generated if left empty"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                Create Tenant
              </button>
              <button
                onClick={cancelEdit}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tenants List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading tenants...</div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No tenants yet. Create your first tenant to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {tenants.map((tenant) => (
            <div key={tenant.id} className="border border-gray-200 rounded-lg p-4">
              {editing === tenant.id ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Netlify Site ID</label>
                    <input
                      type="text"
                      value={formData.netlifySiteId || ''}
                      onChange={(e) => setFormData({ ...formData, netlifySiteId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleUpdate(tenant.id)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{tenant.name}</h3>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-mono">
                        {tenant.id}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Domain:</strong> {tenant.domain || 'Not set'}</p>
                      {tenant.netlifySiteId && (
                        <p><strong>Netlify Site ID:</strong> {tenant.netlifySiteId}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        Created: {new Date(tenant.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="mt-3 flex space-x-2">
                      <a
                        href={`https://live-${tenant.id}.trustcenter.page`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-700 flex items-center"
                      >
                        live-{tenant.id}.trustcenter.page
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => startEdit(tenant)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(tenant.id)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default TenantManager
